import React, { useState, useRef, useEffect, useCallback } from 'react';
import './QrScanner.scss';
import useApiService from 'src/services/useApiService';
import { ApiResponse } from "../../../interfaces";
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// =====================
// Types
// =====================
export interface StudentFormData {
  id: number;
  CCCD: string;
  CMT: string;
  name: string;
  dob: string;
  gender: string;
  address: string;
  registrationDate: string;
}

interface ApiDataQR {
  success: boolean;
  avatar?: string;
  decodedText?: { data: string; type: string }[];
  DT: any[];
  EC: number;
  EM: string;
}

type ScanPhase = 'idle' | 'scanning' | 'qr-found' | 'ocr-running' | 'done' | 'error';

// Extend Window for BarcodeDetector
declare global {
  interface Window {
    BarcodeDetector?: new (opts: { formats: string[] }) => {
      detect: (source: HTMLVideoElement | HTMLCanvasElement | ImageBitmap) => Promise<{ rawValue: string }[]>;
    };
  }
}

// =====================
// Utilities
// =====================
const compressImage = (blob: Blob, maxDim = 1400, quality = 0.82): Promise<Blob> =>
  new Promise((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (Math.max(width, height) > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      canvas.toBlob((r) => resolve(r ?? blob), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(blob); };
    img.src = url;
  });

const captureVideoFrame = (video: HTMLVideoElement, quality = 0.85): Promise<Blob | null> =>
  new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0);
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
  });

const parseDecodedText = (text: string): StudentFormData => {
  const parts = text.split('|');
  return {
    id: 0,
    CCCD: parts[0] || '',
    CMT: parts[1] || '',
    name: parts[2] || '',
    dob: parts[3] || '',
    gender: parts[4] || '',
    address: parts[5] || '',
    registrationDate: parts[6] || '',
  };
};

const PHASE_LABELS: Record<ScanPhase, string> = {
  idle: '',
  scanning: 'Đang quét QR...',
  'qr-found': 'Phát hiện QR! Đang trích xuất...',
  'ocr-running': 'Không tìm thấy QR — đang nhận dạng bằng AI...',
  done: 'Hoàn tất!',
  error: 'Không thể nhận dạng',
};

const FIELD_CONFIG = [
  { key: 'CCCD', label: 'CCCD', icon: 'badge' },
  { key: 'CMT', label: 'CMT', icon: 'credit_card' },
  { key: 'name', label: 'Họ tên', icon: 'person' },
  { key: 'dob', label: 'Ngày sinh', icon: 'cake' },
  { key: 'gender', label: 'Giới tính', icon: 'wc' },
  { key: 'address', label: 'Địa chỉ', icon: 'home' },
  { key: 'registrationDate', label: 'Ngày cấp', icon: 'event' },
] as const;

// =====================
// QrScannerForm — auto-detect QR, auto-fallback to OCR
// =====================
interface QrScannerFormProps {
  onAdd: (data: StudentFormData, avatarBase64: string | null) => void;
}

const QrScannerForm: React.FC<QrScannerFormProps> = ({ onAdd }) => {
  const { post } = useApiService();
  const [formData, setFormData] = useState<StudentFormData>({
    id: 0, CCCD: '', CMT: '', name: '', dob: '', gender: '', address: '', registrationDate: '',
  });
  const [avatar, setAvatar] = useState<string | null>(null);
  const [phase, setPhase] = useState<ScanPhase>('idle');
  const [cameraActive, setCameraActive] = useState(false);
  const [inputMode, setInputMode] = useState<'camera' | 'upload'>('camera');

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanLoopRef = useRef<number>(0);
  const isBusyRef = useRef(false);

  const resetForm = useCallback(() => {
    setFormData({ id: 0, CCCD: '', CMT: '', name: '', dob: '', gender: '', address: '', registrationDate: '' });
    setAvatar(null);
    setPhase('idle');
  }, []);

  // Send image to backend for full processing (QR + avatar + OCR fallback)
  const sendToBackend = useCallback(async (blob: Blob): Promise<boolean> => {
    const compressed = await compressImage(blob);
    const fd = new window.FormData();
    fd.append('image', compressed, 'scan.jpg');
    const response = await post<ApiDataQR>('/api/file/qr/decode', fd);
    const data: ApiDataQR = response.DT?.[0];
    if (response.EC === 0 && data?.success) {
      if (data.avatar) setAvatar(`data:image/jpeg;base64,${data.avatar}`);
      if (data.decodedText?.length && data.decodedText[0]?.data) {
        setFormData(parseDecodedText(data.decodedText[0].data));
        return true;
      }
    }
    return false;
  }, [post]);

  // ----------- Camera auto-scan loop -----------
  const stopCamera = useCallback(() => {
    scanLoopRef.current++;
    setCameraActive(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const startAutoScan = useCallback(async () => {
    resetForm();
    setCameraActive(true);
    setPhase('scanning');

    if (!navigator.mediaDevices?.getUserMedia) {
      setPhase('error');
      toast.error('Trình duyệt không hỗ trợ camera');
      setCameraActive(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        await videoRef.current.play();
      }
    } catch {
      setPhase('error');
      toast.error('Không thể truy cập camera');
      setCameraActive(false);
      return;
    }

    // Auto-scan loop
    const loopId = ++scanLoopRef.current;
    const hasBarcodeDetector = typeof window.BarcodeDetector !== 'undefined';
    let detector: InstanceType<NonNullable<typeof window.BarcodeDetector>> | null = null;
    if (hasBarcodeDetector) {
      detector = new window.BarcodeDetector!({ formats: ['qr_code'] });
    }

    let frameCount = 0;
    const MAX_FRAMES_BEFORE_OCR = hasBarcodeDetector ? 90 : 4; // ~3s for native, ~6s for backend polling
    const POLL_INTERVAL = hasBarcodeDetector ? 33 : 1500; // 30fps native, 1.5s for backend

    const loop = async () => {
      if (loopId !== scanLoopRef.current || !videoRef.current || isBusyRef.current) return;

      frameCount++;

      // Try native BarcodeDetector first (ultra-fast, ~5ms)
      if (detector && videoRef.current.readyState >= 2) {
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes.length > 0) {
            isBusyRef.current = true;
            const qrData = codes[0].rawValue;
            setPhase('qr-found');
            setFormData(parseDecodedText(qrData));

            // Send to backend for avatar extraction
            const frame = await captureVideoFrame(videoRef.current);
            if (frame) {
              const compressed = await compressImage(frame);
              const fd = new window.FormData();
              fd.append('image', compressed, 'scan.jpg');
              try {
                const resp = await post<ApiDataQR>('/api/file/qr/decode', fd);
                const dt: ApiDataQR = resp.DT?.[0];
                if (dt?.avatar) setAvatar(`data:image/jpeg;base64,${dt.avatar}`);
              } catch { /* avatar extraction failed, non-critical */ }
            }

            setPhase('done');
            toast.success('Quét QR thành công!');
            stopCamera();
            isBusyRef.current = false;
            return;
          }
        } catch { /* BarcodeDetector error — fall through */ }
      }

      // Backend polling fallback (when no native BarcodeDetector)
      if (!hasBarcodeDetector && videoRef.current.readyState >= 2) {
        isBusyRef.current = true;
        try {
          const frame = await captureVideoFrame(videoRef.current);
          if (frame) {
            setPhase('scanning');
            const success = await sendToBackend(frame);
            if (success) {
              setPhase('done');
              toast.success('Quét thành công!');
              stopCamera();
              isBusyRef.current = false;
              return;
            }
          }
        } catch { /* continue scanning */ }
        isBusyRef.current = false;
      }

      // After MAX_FRAMES without QR — auto-fallback to OCR
      if (frameCount >= MAX_FRAMES_BEFORE_OCR) {
        isBusyRef.current = true;
        setPhase('ocr-running');
        try {
          const frame = await captureVideoFrame(videoRef.current);
          if (frame) {
            const success = await sendToBackend(frame);
            if (success) {
              setPhase('done');
              toast.success('Nhận dạng AI thành công!');
            } else {
              setPhase('error');
              toast.error('Không thể nhận dạng. Thử lại hoặc tải ảnh lên.');
            }
          }
        } catch {
          setPhase('error');
        }
        stopCamera();
        isBusyRef.current = false;
        return;
      }

      if (loopId === scanLoopRef.current) {
        setTimeout(loop, POLL_INTERVAL);
      }
    };

    setTimeout(loop, 500); // wait for camera to stabilize
  }, [resetForm, sendToBackend, stopCamera, post]);

  // Cleanup on unmount
  useEffect(() => () => { scanLoopRef.current++; stopCamera(); }, [stopCamera]);

  // ----------- Upload mode -----------
  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    resetForm();
    setPhase('scanning');
    try {
      const success = await sendToBackend(e.target.files[0]);
      if (success) {
        setPhase('done');
        toast.success('Nhận dạng thành công!');
      } else {
        setPhase('error');
        toast.error('Không thể nhận dạng ảnh');
      }
    } catch {
      setPhase('error');
    }
    e.target.value = '';
  };

  const handleAddToList = () => {
    if (!formData.CCCD || formData.CCCD === 'null') {
      toast.info('Chưa có dữ liệu hợp lệ để thêm.');
      return;
    }
    onAdd(formData, avatar);
    resetForm();
  };

  const hasData = formData.CCCD && formData.CCCD !== 'null';

  return (
    <div className="qs-scanner-card">
      {/* Header */}
      <div className="qs-header">
        <div className="qs-header-icon">
          <i className="material-icons">qr_code_scanner</i>
        </div>
        <div>
          <h2 className="qs-header-title">Quét CCCD / QR</h2>
          <p className="qs-header-sub">Tự động phát hiện QR hoặc nhận dạng AI</p>
        </div>
      </div>

      {/* Mode tabs */}
      <div className="qs-tabs">
        <button
          type="button"
          className={`qs-tab ${inputMode === 'camera' ? 'qs-tab--active' : ''}`}
          onClick={() => { setInputMode('camera'); if (cameraActive) stopCamera(); }}
        >
          <i className="material-icons">photo_camera</i>
          Camera
        </button>
        <button
          type="button"
          className={`qs-tab ${inputMode === 'upload' ? 'qs-tab--active' : ''}`}
          onClick={() => { setInputMode('upload'); if (cameraActive) stopCamera(); }}
        >
          <i className="material-icons">upload_file</i>
          Tải ảnh lên
        </button>
      </div>

      {/* Camera mode */}
      {inputMode === 'camera' && (
        <div className="qs-camera-section">
          <div className="qs-camera-viewport">
            <video ref={videoRef} className="qs-video" autoPlay playsInline muted />
            {cameraActive && phase === 'scanning' && (
              <div className="qs-scan-overlay">
                <div className="qs-scan-frame">
                  <div className="qs-scan-line" />
                </div>
              </div>
            )}
            {!cameraActive && phase === 'idle' && (
              <div className="qs-camera-placeholder">
                <i className="material-icons">videocam</i>
                <span>Nhấn bắt đầu để quét</span>
              </div>
            )}
          </div>

          <div className="qs-camera-actions">
            {!cameraActive ? (
              <button type="button" className="qs-btn qs-btn--primary qs-btn--lg" onClick={startAutoScan} disabled={phase === 'ocr-running'}>
                <i className="material-icons">qr_code_scanner</i>
                Bắt đầu quét
              </button>
            ) : (
              <button type="button" className="qs-btn qs-btn--outline" onClick={() => { stopCamera(); setPhase('idle'); }}>
                <i className="material-icons">stop</i>
                Dừng
              </button>
            )}
          </div>
        </div>
      )}

      {/* Upload mode */}
      {inputMode === 'upload' && (
        <div className="qs-upload-section">
          <label className="qs-dropzone" htmlFor="qs-file-input">
            <i className="material-icons">cloud_upload</i>
            <span className="qs-dropzone-text">Chọn hoặc kéo thả ảnh CCCD / QR</span>
            <span className="qs-dropzone-hint">JPG, PNG — tối đa 10MB</span>
            <input
              id="qs-file-input"
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="qs-file-hidden"
              disabled={phase === 'scanning' || phase === 'ocr-running'}
            />
          </label>
        </div>
      )}

      {/* Status bar */}
      {phase !== 'idle' && (
        <div className={`qs-status qs-status--${phase}`}>
          {(phase === 'scanning' || phase === 'ocr-running' || phase === 'qr-found') && (
            <div className="qs-status-spinner" />
          )}
          {phase === 'done' && <i className="material-icons">check_circle</i>}
          {phase === 'error' && <i className="material-icons">error_outline</i>}
          <span>{PHASE_LABELS[phase]}</span>
        </div>
      )}

      {/* Result card */}
      {hasData && (
        <div className="qs-result">
          <div className="qs-result-header">
            {avatar && <img src={avatar} alt="Avatar" className="qs-result-avatar" />}
            <div className="qs-result-name">
              <h3>{formData.name || '—'}</h3>
              <span className="qs-result-cccd">{formData.CCCD}</span>
            </div>
          </div>

          <div className="qs-result-fields">
            {FIELD_CONFIG.map(({ key, label, icon }) => (
              <div className="qs-field" key={key}>
                <i className="material-icons qs-field-icon">{icon}</i>
                <div className="qs-field-content">
                  <span className="qs-field-label">{label}</span>
                  <span className="qs-field-value">{(formData as any)[key] || '—'}</span>
                </div>
              </div>
            ))}
          </div>

          <button type="button" className="qs-btn qs-btn--success qs-btn--lg qs-btn--full" onClick={handleAddToList}>
            <i className="material-icons">add_circle</i>
            Thêm vào danh sách
          </button>
        </div>
      )}
    </div>
  );
};

// =====================
// QrEntriesTable
// =====================
interface QrEntriesTableProps {
  entries: StudentFormData[];
  avatars: Record<string, string | null>;
  onUpdate: (data: StudentFormData) => void;
  onDelete: (id: number) => void;
}

const QrEntriesTable: React.FC<QrEntriesTableProps> = ({ entries, avatars, onUpdate, onDelete }) => {
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<StudentFormData | null>(null);

  const startEdit = (row: StudentFormData) => { setEditId(row.CCCD); setDraft({ ...row }); };
  const cancelEdit = () => { setEditId(null); setDraft(null); };
  const saveEdit = () => { if (!draft) return; onUpdate(draft); toast.success('Cập nhật thành công'); cancelEdit(); };

  if (entries.length === 0) {
    return (
      <div className="qs-empty-table">
        <i className="material-icons">inbox</i>
        <p>Chưa có bản ghi nào</p>
      </div>
    );
  }

  return (
    <div className="qs-table-wrap">
      <table className="qs-table">
        <thead>
          <tr>
            <th>#</th>
            <th></th>
            <th>CCCD</th>
            <th>CMT</th>
            <th>Họ tên</th>
            <th>Ngày sinh</th>
            <th>Giới tính</th>
            <th>Địa chỉ</th>
            <th>Ngày cấp</th>
            <th>Hành động</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((row, idx) => {
            const isEditing = editId === row.CCCD;
            const cur = isEditing && draft ? draft : row;
            return (
              <tr key={row.id} className={isEditing ? 'qs-row--editing' : ''}>
                <td>{idx + 1}</td>
                <td>
                  {avatars[row.CCCD]
                    ? <img src={avatars[row.CCCD]!} alt="" className="qs-table-avatar" />
                    : <div className="qs-table-avatar-placeholder"><i className="material-icons">person</i></div>}
                </td>
                {(['CCCD', 'CMT', 'name', 'dob', 'gender', 'address', 'registrationDate'] as const).map((field) => (
                  <td key={field}>
                    {isEditing
                      ? <input className="qs-table-input" value={cur[field]} onChange={(e) => setDraft({ ...cur, [field]: e.target.value })} />
                      : row[field]}
                  </td>
                ))}
                <td>
                  <div className="qs-table-actions">
                    {isEditing ? (
                      <>
                        <button className="qs-btn qs-btn--sm qs-btn--success" onClick={saveEdit}><i className="material-icons">check</i></button>
                        <button className="qs-btn qs-btn--sm qs-btn--outline" onClick={cancelEdit}><i className="material-icons">close</i></button>
                      </>
                    ) : (
                      <>
                        <button className="qs-btn qs-btn--sm qs-btn--outline" onClick={() => startEdit(row)}><i className="material-icons">edit</i></button>
                        <button className="qs-btn qs-btn--sm qs-btn--danger" onClick={() => onDelete(row.id)}><i className="material-icons">delete</i></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// =====================
// QrScannerPage — parent
// =====================
type CourseQR = { id: number; name: string; description?: string | null; createdAt?: string; updatedAt?: string; type?: string; code?: string | null };

const QR_TYPES = ['CCCD', 'GPLX', 'BANK_VIETQR', 'BILL_QR', 'E_GOV', 'HEALTH_QR', 'E_TICKET', 'WAYBILL', 'PRODUCT_QR', 'VOUCHER', 'OTHER'];

const QrScannerPage: React.FC = () => {
  const { get, post, put, del } = useApiService();

  const [entries, setEntries] = useState<StudentFormData[]>([]);
  const [avatars, setAvatars] = useState<Record<string, string | null>>({});
  const [courses, setCourses] = useState<CourseQR[]>([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [newCourseName, setNewCourseName] = useState('');
  const [newCourseDesc, setNewCourseDesc] = useState('');
  const [newCourseType, setNewCourseType] = useState('CCCD');
  const [showCreateCourse, setShowCreateCourse] = useState(false);

  const fetchCourses = useCallback(async () => {
    try {
      const r = await get<ApiResponse>('/api/courseqr');
      setCourses(r.EC === 0 && r.DT ? r.DT as CourseQR[] : []);
    } catch { setCourses([]); }
  }, [get]);

  useEffect(() => { void fetchCourses(); }, [fetchCourses]);

  const fetchEnrolled = useCallback(async () => {
    if (!selectedCourse) { setEntries([]); return; }
    try {
      const r = await get<ApiResponse<CourseQR[]>>(`/api/qr/list?courseId=${selectedCourse}`);
      if (r.EC === 0 && r.DT) {
        setEntries(
          r.DT.map((e) => {
            try {
              if (!e?.code) return null;
              return { ...JSON.parse(e.code), id: e.id };
            } catch { return null; }
          }).filter((x): x is StudentFormData => x !== null)
        );
      } else setEntries([]);
    } catch { setEntries([]); }
  }, [selectedCourse, get]);

  useEffect(() => { void fetchEnrolled(); }, [fetchEnrolled]);

  const handleAdd = async (data: StudentFormData, avatarBase64: string | null) => {
    if (!selectedCourse) { toast.warn('Chọn khóa trước khi thêm'); return; }
    try {
      const r = await post<ApiResponse>('/api/qr', { courseId: selectedCourse, code: data });
      if (r.EC === 0) {
        toast.success(`Đã thêm ${data.name}`);
        if (avatarBase64) setAvatars((p) => ({ ...p, [data.CCCD]: avatarBase64 }));
        fetchEnrolled();
      } else toast.error(r.EM || 'Thêm thất bại');
    } catch { toast.error('Lỗi khi thêm'); }
  };

  const handleUpdate = async (updated: StudentFormData) => {
    if (!selectedCourse) { toast.warn('Chọn khóa trước'); return; }
    try {
      const r = await put<ApiResponse>('/api/qr/update', { studentData: updated });
      if (r.EC === 0) { toast.success('Cập nhật thành công'); fetchEnrolled(); }
      else toast.error(r.EM || 'Cập nhật thất bại');
    } catch { toast.error('Lỗi cập nhật'); }
  };

  const handleDelete = async (id: number) => {
    if (!selectedCourse) { toast.warn('Chọn khóa trước'); return; }
    try {
      const r = await del<ApiResponse>(`/api/qr/${encodeURIComponent(id)}`);
      if (r.EC === 0) { toast.success('Đã xóa'); fetchEnrolled(); }
      else toast.error(r.EM || 'Xóa thất bại');
    } catch { toast.error('Lỗi xóa'); }
  };

  const handleCreateCourse = async () => {
    if (!newCourseName.trim()) { toast.warning('Nhập tên khóa'); return; }
    try {
      const r = await post<ApiResponse>('/api/courseQR/add', { name: newCourseName.trim(), description: newCourseDesc || null, type: newCourseType });
      if (r.EC === 0) {
        toast.success('Tạo khóa thành công');
        setNewCourseName(''); setNewCourseDesc(''); setNewCourseType('CCCD');
        setShowCreateCourse(false); fetchCourses();
      } else toast.error(r.EM || 'Tạo thất bại');
    } catch { toast.error('Tạo khóa thất bại'); }
  };

  const handleExportToExcel = () => {
    if (entries.length === 0) { toast.info('Không có dữ liệu'); return; }
    const ws = XLSX.utils.json_to_sheet(entries.map((e, i) => ({
      STT: i + 1, CCCD: e.CCCD, CMT: e.CMT, 'Họ tên': e.name, 'Ngày sinh': e.dob, 'Giới tính': e.gender, 'Địa chỉ': e.address, 'Ngày cấp': e.registrationDate,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'QR Data');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `QR_${new Date().toLocaleDateString('vi-VN')}.xlsx`);
    toast.success('Đã xuất Excel!');
  };

  return (
    <div className="qs-page">
      <div className="qs-page-header">
        <h1 className="qs-page-title">
          <i className="material-icons">qr_code</i>
          Quét QR / CCCD
        </h1>
      </div>

      <QrScannerForm onAdd={handleAdd} />

      {/* Course selector */}
      <div className="qs-course-section">
        <div className="qs-course-bar">
          <div className="qs-course-select-wrap">
            <i className="material-icons">folder</i>
            <select className="qs-select" value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)}>
              <option value="">-- Chọn khóa --</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="qs-course-actions">
            <button type="button" className="qs-btn qs-btn--outline qs-btn--sm" onClick={() => setShowCreateCourse(!showCreateCourse)}>
              <i className="material-icons">{showCreateCourse ? 'close' : 'add'}</i>
              {showCreateCourse ? 'Hủy' : 'Tạo khóa'}
            </button>
            <button type="button" className="qs-btn qs-btn--success qs-btn--sm" onClick={handleExportToExcel}>
              <i className="material-icons">download</i>
              Xuất Excel
            </button>
          </div>
        </div>

        {showCreateCourse && (
          <div className="qs-create-course">
            <input className="qs-input" placeholder="Tên khóa" value={newCourseName} onChange={(e) => setNewCourseName(e.target.value)} />
            <input className="qs-input" placeholder="Mô tả (tùy chọn)" value={newCourseDesc} onChange={(e) => setNewCourseDesc(e.target.value)} />
            <select className="qs-select" value={newCourseType} onChange={(e) => setNewCourseType(e.target.value)}>
              {QR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <button type="button" className="qs-btn qs-btn--primary" onClick={handleCreateCourse}>
              <i className="material-icons">save</i> Lưu
            </button>
          </div>
        )}
      </div>

      {/* Entries table */}
      <div className="qs-entries-section">
        <div className="qs-entries-header">
          <h2>
            <i className="material-icons">list_alt</i>
            Danh sách ({entries.length})
          </h2>
        </div>
        <QrEntriesTable entries={entries} avatars={avatars} onUpdate={handleUpdate} onDelete={handleDelete} />
      </div>
    </div>
  );
};

export default QrScannerPage;
